import { ScrollView, View } from 'react-native';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared/components/button';

import { BaseModal, BaseModalProps, BaseModalRef } from 'shared/components/base-modal';
import { notify } from 'shared/library/notify';

interface ConfirmModalProps extends BaseModalProps {
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => Promise<void | any>;
  onCancel?: () => void;
}

export type ConfirmModalRef = BaseModalRef;

export const ConfirmModal = forwardRef<ConfirmModalRef, ConfirmModalProps>(
  (
    { title, description, confirmLabel, cancelLabel, onConfirm, onCancel, children, trigger },
    ref,
  ) => {
    const modalRef = useRef<BaseModalRef>(null);
    const { t } = useTranslation('components');

    useImperativeHandle(ref, () => ({
      open: modalRef.current!.open,
      close: modalRef.current!.close,
    }));

    title = title || t('confirm_modal.title');
    description = description || t('confirm_modal.description');
    confirmLabel = confirmLabel || t('confirm_modal.continue');
    cancelLabel = cancelLabel || t('confirm_modal.cancel');

    return (
      <BaseModal
        ref={modalRef}
        trigger={trigger}
        title={title}
        description={description}
        footer={
          <View className="flex-row gap-3">
            <Button
              text={cancelLabel}
              color={'white'}
              onPress={() => {
                modalRef.current?.close();
                onCancel?.();
              }}
            />

            <View className="flex-1">
              <Button
                text={confirmLabel}
                center={true}
                onPress={() => {
                  onConfirm()
                    .then(() => modalRef.current?.close())
                    .catch((e) => notify.error(e, 'confirm-modal.tsx - onConfirm'));
                }}
              />
            </View>
          </View>
        }
      >
        {!!children && (
          <ScrollView className={'max-h-[50dvh] flex-1'} overScrollMode="never">
            {children}
          </ScrollView>
        )}
      </BaseModal>
    );
  },
);

ConfirmModal.displayName = 'ConfirmModal';
